import type { CSSProperties } from 'react'
import { OverlayScrollArea } from '../components/OverlayScrollbar'
import { watchTime } from '../provider/providerClient'
import type { ProviderOverview, TitleRow } from '../provider/providerClient'
import { slice } from '../analytics/slice'
import type { SliceAxes } from '../analytics/slice'

/**
 * OVERVIEW — the headline position for the selected period (dashboard plan D3, metrics per D11).
 *
 * ★★ THE PIE IS A CONIC-GRADIENT, NOT A CHART LIBRARY. v1 drew every one of its visuals with
 * three hand-rolled primitives — a conic-gradient pie, div bars, and CSS-grid pseudo-tables — and
 * that instinct is worth carrying forward: this app has three runtime dependencies and a charting
 * library would earn its place only by doing something these cannot.
 *
 * ★★ WHAT THE PLATFORM SIDE MAY SAY (David, 2026-07-23: providers DO see whole-platform figures):
 * TOTALS and this supplier's share of them — scalars. Never a leaderboard of other suppliers'
 * titles. v1 blanked competitors' names but still drew their rows at 20% opacity, which leaks the
 * shape and magnitude of a rival's performance; the fix is not to blank a list, it is not to
 * build one. The server enforces this by having no method that returns another org's titles.
 *
 * ⚠ WATCH TIME IS APPROXIMATE BY CONSTRUCTION — pings × 3 seconds — so it is never printed to the
 * second, and the page says so once rather than implying precision it does not have.
 */

/** ★ Headings are Primary:VeryDark, not --foreground (David, 2026-07-27) — the brand green
 *  carries the heading level so the near-black is left to body text alone. */
const h1: CSSProperties = {
  fontSize: 'var(--fs-title)',
  fontWeight: 600,
  margin: '0 0 2px',
  color: 'var(--primary-verydark)',
}
const sub: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)', margin: '0 0 20px' }
const muted: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)' }
const tiny: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-xs)' }

const card: CSSProperties = {
  flex: '1 1 200px',
  background: 'var(--card)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '14px 16px',
}
const stat: CSSProperties = { fontFamily: 'var(--font-number)', fontSize: 30, lineHeight: 1.1, fontWeight: 600 }
const statLabel: CSSProperties = { fontSize: 'var(--fs-sm)', fontWeight: 500, marginTop: 4 }

const panel: CSSProperties = { ...card, flex: '1 1 100%', marginBottom: 12 }
const panelHead: CSSProperties = {
  ...tiny,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 10,
}

const shareRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }

/**
 * ★ The pie: one div, `border-radius: 50%`, filled by a conic-gradient. Two slices only — yours
 * and everyone else's — which is the only comparison this portal is allowed to draw.
 */
function SharePie({ percent, ariaLabel }: { percent: number; ariaLabel: string }) {
  const angle = Math.max(0, Math.min(100, percent)) * 3.6
  return (
    <div
      role="img"
      aria-label={ariaLabel}
      style={{
        width: 132,
        height: 132,
        borderRadius: '50%',
        flex: '0 0 auto',
        background: `conic-gradient(var(--primary) 0deg ${angle}deg, var(--border) ${angle}deg 360deg)`,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      <div
        style={{
          width: 92,
          height: 92,
          borderRadius: '50%',
          background: 'var(--card)',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--font-number)',
          fontWeight: 600,
          fontSize: 20,
        }}
      >
        {percent}%
      </div>
    </div>
  )
}

/**
 * ★★ A leaderboard bar, MEASURED AGAINST THE DENOMINATOR THE COMPARE AXIS CHOSE — your own total,
 * or everything watched on Yoreflix. That is the axis doing real work here: at "share of yours"
 * the bars divide up your quarter, at "share of Yoreflix" they show how much of the whole platform
 * each title actually is. The percentage is printed, so the scale is never a mystery.
 *
 * ⚠ IT USED TO SCALE TO THE TOP ROW, which made the Compare axis a control that changed nothing on
 * this screen (David, 2026-07-23: "Compare dropdown seems to have no effect") — the exact sin the
 * FilterBar's own comment warns about. Scaling to a stated total costs a little discrimination
 * between the small rows and buys an axis that means something.
 *
 * ⚠ v1's version had a hard-coded maximum (40 views / 3000 seconds) that never recomputed from the
 * data, so anything bigger simply clipped, and it grew a manual zoom control to work around its own
 * scale. A denominator taken from the figures is not that.
 */
function Leaderboard({
  title,
  rows,
  value,
  format,
  denominator,
}: {
  title: string
  rows: TitleRow[]
  value: (row: TitleRow) => number
  format: (row: TitleRow) => string
  /** Total the bars are a share OF — follows the Compare axis. */
  denominator: number
}) {
  const top = rows.slice(0, 10)
  // ⚠ Never divide by a total we have not got: fall back to the leader rather than to zero, which
  // would draw every bar full width and quietly claim each title was the whole platform.
  const max = denominator > 0 ? denominator : Math.max(1, ...top.map(value))

  return (
    <div style={panel}>
      <div style={panelHead}>{title}</div>
      {top.length === 0 && <div style={muted}>Nothing matches this slice.</div>}
      {/* ★★ LABEL ABOVE THE BAR, VALUE RIGHT-ALIGNED, BAR FULL WIDTH BENEATH — and this is the
          layout that also fixes v1's worst flaw. v1 puts the label to the RIGHT of the bar, so at
          per-episode depth, where the label is a full breadcrumb, the text overruns the bar and
          the percentage inside it becomes unreadable. Arranged in `styles/layout.css`. */}
      <div className="dash-rank">
        {top.map((row) => (
          <div key={row.ContentKey} className="dash-rank-row">
            <div className="dash-rank-head" style={{ fontSize: 'var(--fs-sm)' }}>
              <span className="dash-rank-name" title={row.Title}>
                {row.Title}
              </span>
              <span className="dash-rank-value">
                {format(row)}
                <span style={{ ...tiny, marginLeft: 8 }}>
                  {Math.round((value(row) / max) * 1000) / 10}%
                </span>
              </span>
            </div>
            <div className="dash-rank-track">
              <div className="dash-rank-fill" style={{ width: `${Math.max(2, (value(row) / max) * 100)}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function OverviewScreen({
  data,
  titles,
  axes,
  error,
}: {
  data: ProviderOverview | null
  titles: TitleRow[]
  axes: SliceAxes
  error: string
}) {
  if (error) return <div className="dash-page"><div style={{ ...muted, color: 'var(--destructive)' }}>{error}</div></div>
  if (!data) return <div className="dash-page"><div style={muted}>Loading…</div></div>

  if (!data.Linked) {
    return (
      <div className="dash-page">
        <div style={{ ...card, borderColor: 'var(--warning)', maxWidth: 620 }}>
          <p style={{ margin: '0 0 8px', fontWeight: 500 }}>Your account is not linked to an organization yet.</p>
          <p style={{ ...muted, margin: 0 }}>
            That is something we set up at our end. Email{' '}
            <a href="mailto:info@yoreflix.com" style={{ color: 'var(--primary)' }}>info@yoreflix.com</a> and we will
            sort it out.
          </p>
        </div>
      </div>
    )
  }

  const yours = data.Yours ?? { Views: 0, WatchSeconds: 0, TitlesWatched: 0, TitlesInCatalogue: 0 }
  const platform = data.Platform ?? { Views: 0, WatchSeconds: 0 }
  const share = data.Share ?? { WatchTimePercent: 0, ViewsPercent: 0 }
  /**
   * ★★ THE AXES DRIVE THIS SCREEN TOO. One control bar re-cutting every panel at once is the
   * whole point of v1's shape — leaderboards that ignored the filter while the tiles obeyed it
   * would be two answers to two different questions sitting side by side.
   */
  const watchedByTime = slice(titles, axes, 'watch').filter((t) => t.WatchSeconds > 0)
  const watchedByViews = slice(titles, axes, 'views').filter((t) => t.Views > 0)
  // Said on the panel itself, so a percentage is never floating without its denominator.
  const scopeLabel = axes.scope === 'platform' ? 'share of Yoreflix' : 'share of yours'
  // The doughnut's "share of yours" reading: how much of your catalogue got watched at all.
  const coverage =
    yours.TitlesInCatalogue > 0 ? Math.round((yours.TitlesWatched / yours.TitlesInCatalogue) * 1000) / 10 : 0

  return (
    <OverlayScrollArea axis="y">
      <div className="dash-page">
        <h1 style={h1}>{data.Period?.Label ?? 'This period'}</h1>
        <p style={sub}>How your titles performed.</p>

        <div className="dash-tiles" style={{ marginBottom: 18 }}>
          <div style={card}>
            <div style={stat}>{yours.Views.toLocaleString()}</div>
            <div style={statLabel}>{yours.Views === 1 ? 'view' : 'views'}</div>
            {/* ⚠ Said plainly: a "view" is DEFINED, not recorded — the write path only emits
                heartbeats. Leaving it unexplained invites a supplier to compare it with a number
                from somewhere else that means something different. */}
            <div style={tiny}>one person, one title, one day</div>
          </div>
          <div style={card}>
            <div style={stat}>{watchTime(yours.WatchSeconds)}</div>
            <div style={statLabel}>watch time</div>
            <div style={tiny}>measured in 3-second samples, so approximate</div>
          </div>
          <div style={card}>
            <div style={stat}>
              {yours.TitlesWatched}
              <span style={{ fontSize: 18, color: 'var(--muted-foreground)' }}> / {yours.TitlesInCatalogue}</span>
            </div>
            <div style={statLabel}>of your titles were watched</div>
            <div style={tiny}>everything you have in the catalogue</div>
          </div>
        </div>

        {/* ★★ THE DOUGHNUT FOLLOWS THE COMPARE AXIS, because a fixed one made the axis look
            broken here (David, 2026-07-23: "the bars change but the doughnut chart doesn't"). It
            cannot simply take the platform share at both settings: "your share of yourself" is
            100% and says nothing. So the whole panel reframes — at platform scope it is your slice
            of Yoreflix; at your-own scope it is how much of your catalogue got watched. Each
            proportion is honest to the word the axis is showing. */}
        {axes.scope === 'platform' ? (
          <div style={panel}>
            <div style={panelHead}>Your share of Yoreflix</div>
            <div style={shareRow}>
              <SharePie
                percent={share.WatchTimePercent}
                ariaLabel={`Your share of platform watch time: ${share.WatchTimePercent}%`}
              />
              <div>
                <div style={{ fontSize: 'var(--fs-sm)', marginBottom: 6 }}>
                  <strong>{watchTime(yours.WatchSeconds)}</strong> of{' '}
                  <strong>{watchTime(platform.WatchSeconds)}</strong> watched across the platform.
                </div>
                <div style={muted}>
                  {share.ViewsPercent}% of all views ({yours.Views.toLocaleString()} of{' '}
                  {platform.Views.toLocaleString()}).
                </div>
                {/* ★ The platform figure is a TOTAL and stays one — no other supplier is named
                    anywhere in this portal, by design rather than by redaction. */}
                <div style={{ ...tiny, marginTop: 8, maxWidth: 420 }}>
                  Platform figures are totals across all content on Yoreflix.
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div style={panel}>
            <div style={panelHead}>Your catalogue this period</div>
            <div style={shareRow}>
              <SharePie
                percent={coverage}
                ariaLabel={`${coverage}% of your catalogue was watched this period`}
              />
              <div>
                <div style={{ fontSize: 'var(--fs-sm)', marginBottom: 6 }}>
                  <strong>{yours.TitlesWatched}</strong> of your{' '}
                  <strong>{yours.TitlesInCatalogue}</strong> titles were watched this period.
                </div>
                <div style={muted}>
                  The leaderboards below divide up your own {watchTime(yours.WatchSeconds)} and{' '}
                  {yours.Views.toLocaleString()} views.
                </div>
                <div style={{ ...tiny, marginTop: 8, maxWidth: 420 }}>
                  Switch Compare to “Share of Yoreflix” to see how you sit against the whole platform.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ★★ BOTH METRICS ALWAYS ON SCREEN, NEVER A TOGGLE — v1 puts Views and Watch Time side by
            side and that is what makes it read as slicing rather than navigating. At phone width
            they stack (see `.dash-metrics`): both still present in one scroll, still no toggle. */}
        <div className="dash-metrics">
          <Leaderboard
            title={`Most watched — by views · ${scopeLabel}`}
            rows={watchedByViews}
            value={(r) => r.Views}
            format={(r) => `${r.Views.toLocaleString()}`}
            denominator={axes.scope === 'platform' ? platform.Views : yours.Views}
          />
          <Leaderboard
            title={`Most watched — by watch time · ${scopeLabel}`}
            rows={watchedByTime}
            value={(r) => r.WatchSeconds}
            format={(r) => watchTime(r.WatchSeconds)}
            denominator={axes.scope === 'platform' ? platform.WatchSeconds : yours.WatchSeconds}
          />
        </div>
      </div>
    </OverlayScrollArea>
  )
}
